/**
 * Search query grammar (spec §14). Tokens split on whitespace; `"…"` and `«…»` group;
 * `key:value` operators with Arabic aliases; unknown/empty operators stay in the free text.
 */
import type { SearchChip } from '$lib/types';

export interface ParsedQuery {
  text: string;
  /** Number of recognised operators. */
  ops: number;
  from?: string;
  to?: string;
  subject?: string;
  unseen?: true;
  seen?: true;
  flagged?: true;
  hasAttachment?: true;
  list?: true;
  personal?: true;
  /** `"all"` or a folder name. */
  scope?: string;
  /** `YYYY-MM-DD`. */
  after?: string;
  before?: string;
  label?: string;
}

export const QUERY_KEYS = new Set(['from', 'to', 'subject', 'is', 'has', 'in', 'before', 'after', 'label', 'تسمية']);

const IS_VALUES: Record<string, 'unseen' | 'seen' | 'flagged' | 'list' | 'personal'> = {
  unread: 'unseen',
  unseen: 'unseen',
  read: 'seen',
  seen: 'seen',
  starred: 'flagged',
  flagged: 'flagged',
  'غير-مقروءة': 'unseen',
  مقروءة: 'seen',
  مميزة: 'flagged',
  list: 'list',
  newsletter: 'list',
  نشرة: 'list',
  نشرات: 'list',
  personal: 'personal',
  primary: 'personal',
  شخصي: 'personal',
  شخصية: 'personal',
};

const HAS_VALUES: Record<string, 'hasAttachment'> = {
  attachment: 'hasAttachment',
  attachments: 'hasAttachment',
  file: 'hasAttachment',
  مرفق: 'hasAttachment',
  مرفقات: 'hasAttachment',
};

/** Accepts `YYYY-M-D` or `D/M/YYYY` (`.` allowed), validates the calendar date → `YYYY-MM-DD`. */
export function normalizeDate(s: string): string | null {
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  const dmy = s.match(/^(\d{1,2})[/.](\d{1,2})[/.](\d{4})$/);
  let y: number;
  let m: number;
  let d: number;
  if (iso) {
    y = +iso[1]!;
    m = +iso[2]!;
    d = +iso[3]!;
  } else if (dmy) {
    d = +dmy[1]!;
    m = +dmy[2]!;
    y = +dmy[3]!;
  } else return null;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${y}-${pad(m)}-${pad(d)}`;
}

/** Tokeniser: whitespace-separated, with `"…"` and `«…»` grouping (quotes are removed). */
export function tokenize(q: string): string[] {
  const out: string[] = [];
  let cur = '';
  let closing = '';
  for (const ch of q) {
    if (closing) {
      if (ch === closing) closing = '';
      else cur += ch;
      continue;
    }
    if (ch === '"' || ch === '«' || ch === '»') {
      closing = ch === '«' ? '»' : ch;
      continue;
    }
    if (/\s/.test(ch)) {
      if (cur) {
        out.push(cur);
        cur = '';
      }
      continue;
    }
    cur += ch;
  }
  if (cur) out.push(cur);
  return out;
}

export function parseQuery(q: string): ParsedQuery {
  const out: ParsedQuery = { text: '', ops: 0 };
  const free: string[] = [];
  for (const tok of tokenize(q)) {
    const i = tok.indexOf(':');
    const key = i > 0 ? tok.slice(0, i).toLowerCase() : '';
    if (!key || !QUERY_KEYS.has(key)) {
      free.push(tok);
      continue;
    }
    const value = tok.slice(i + 1).trim();
    if (!value) {
      free.push(tok);
      continue;
    }
    if (key === 'is' || key === 'has') {
      const field = key === 'is' ? IS_VALUES[value.toLowerCase()] : HAS_VALUES[value.toLowerCase()];
      if (!field) {
        free.push(tok);
        continue;
      }
      out[field] = true;
      out.ops++;
      continue;
    }
    if (key === 'before' || key === 'after') {
      const d = normalizeDate(value);
      if (!d) {
        free.push(tok);
        continue;
      }
      if (key === 'before') out.before = d;
      else out.after = d;
      out.ops++;
      continue;
    }
    if (key === 'label' || key === 'تسمية') {
      out.label = value.slice(0, 40);
      out.ops++;
      continue;
    }
    if (key === 'in') {
      out.scope = value.toLowerCase() === 'all' || value === 'الكل' ? 'all' : value;
      out.ops++;
      continue;
    }
    const v = value.slice(0, 200);
    if (key === 'from') out.from = v;
    else if (key === 'to') out.to = v;
    else out.subject = v;
    out.ops++;
  }
  if (out.seen && out.unseen) {
    delete out.seen;
    delete out.unseen;
    out.ops -= 2;
  }
  out.text = free.join(' ').trim();
  return out;
}

/** A query is active when the free text has ≥ 2 chars or any operator was recognised. */
export function isActiveQuery(q: ParsedQuery): boolean {
  return q.text.trim().length >= 2 || q.ops > 0;
}

/** Chips shown above the list, one per operator (`key` maps to an `op_*` i18n label). */
export function queryChips(q: ParsedQuery): SearchChip[] {
  const chips: SearchChip[] = [];
  if (q.from) chips.push({ key: 'from', label: q.from });
  if (q.to) chips.push({ key: 'to', label: q.to });
  if (q.subject) chips.push({ key: 'subject', label: q.subject });
  if (q.unseen) chips.push({ key: 'unread', label: '' });
  if (q.seen) chips.push({ key: 'read', label: '' });
  if (q.flagged) chips.push({ key: 'starred', label: '' });
  if (q.hasAttachment) chips.push({ key: 'attachment', label: '' });
  if (q.list) chips.push({ key: 'list', label: '' });
  if (q.personal) chips.push({ key: 'personal', label: '' });
  if (q.scope) chips.push({ key: q.scope === 'all' ? 'inall' : 'in', label: q.scope === 'all' ? '' : q.scope });
  if (q.label) chips.push({ key: 'label', label: q.label });
  if (q.after) chips.push({ key: 'after', label: q.after });
  if (q.before) chips.push({ key: 'before', label: q.before });
  return chips;
}

/** Chip key → i18n label key. */
export const CHIP_LABEL_KEYS: Readonly<Record<string, string>> = Object.freeze({
  from: 'op_from',
  to: 'op_to',
  subject: 'op_subject',
  unread: 'op_unread',
  read: 'op_read',
  starred: 'op_starred',
  attachment: 'op_attachment',
  in: 'op_in',
  inall: 'op_inall',
  after: 'op_after',
  before: 'op_before',
  label: 'op_label',
  followup: 'followupChip',
  list: 'op_list',
  personal: 'op_personal',
});

/** Quick-filter chips shown when the box is empty (spec §9 item 3). */
export function quickFilters(user: string | null): { q: string; label: string }[] {
  return [
    { q: 'is:unread', label: 'op_unread' },
    { q: 'has:attachment', label: 'op_attachment' },
    { q: 'is:starred', label: 'op_starred' },
    { q: 'is:personal', label: 'op_personal' },
    { q: 'is:list', label: 'op_list' },
    ...(user ? [{ q: `to:${user}`, label: 'op_tome' }] : []),
  ];
}
